import useSWR, { mutate } from "swr";
import { API_URL } from "../config/endpoints";
import { QuoteRequest } from "@/containers/BookingConfirmationContainer/types";

interface UseQuoteResult {
  data: any;
  error: string | null;
  couponError: string | null;
  isValidating: boolean;
  applyCoupon: () => Promise<void>;
  loading: boolean;
}

function transformPayload<T extends object>(payload: T): T {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    (acc as any)[key] = value === null ? undefined : value; // Use type assertion
    return acc;
  }, {} as T);
}

const fetchQuote = async (url: string, quotePayload: QuoteRequest) => {
  const formattedPayload = transformPayload(quotePayload);

  console.log("Sending Quote Payload:", formattedPayload);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(formattedPayload),
  });

  const responseData = await response.json();

  if (!response.ok) {
    console.error("Quote API Error:", responseData);

    if (responseData?.violations && Array.isArray(responseData.violations)) {
      return Promise.reject(responseData);
    }

    throw new Error(responseData?.message || "Failed to fetch quote");
  }

  return responseData;
};

const useQuote = (
  propertyId: string,
  quotePayload: QuoteRequest,
  enabled = true
): UseQuoteResult => {
  const quoteUrl = `${API_URL}/guest/properties/${propertyId}/quote`;

  const { data, error, isValidating } = useSWR(
    enabled ? [quoteUrl, JSON.stringify(quotePayload)] : null,
    ([url, payload]) => fetchQuote(url, JSON.parse(payload)), // Avoids constant re-fetching
    { revalidateOnFocus: false, revalidateOnReconnect: false } // Stops automatic re-fetching
  );

  let couponError: string | null = null;

  // Extract coupon-specific validation error
  if (error && typeof error === "object" && error.violations) {
    const couponViolation = error.violations.find(
      (violation: any) => violation.path === "coupon"
    );

    if (couponViolation) {
      couponError = "❗ Invalid coupon. Try with a valid one.";
    }
  }

  const applyCoupon = async () => {
    try {
      if (quotePayload.coupon) {
        await mutate([quoteUrl, JSON.stringify(quotePayload)]);
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
    }
  };

  return {
    data,
    error: error?.message ?? null,
    couponError,
    isValidating,
    applyCoupon,
    loading: isValidating,
  };
};

export default useQuote;
